import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = async () => {
    await this.#checkTierDiscount();
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  async open() {
    await this.#checkTierDiscount();
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * Evaluates total item quantity and applies/clears the matching tier discount.
   * Tiers are "at least N", so the highest qualifying tier wins.
   */
  async #checkTierDiscount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const count = cart.item_count;

      const targetDiscount = this.#getTierDiscountCode(count);

      // Check current applied discount code via cookies to prevent redundant requests
      const getDiscountCookie = () => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; discount_code=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
      };

      const currentDiscount = getDiscountCookie();

      // Apply required tier discount or clear if quantity doesn't qualify for any tier
      if (targetDiscount && currentDiscount !== targetDiscount) {
        await fetch(`/discount/${targetDiscount}`);
      } else if (!targetDiscount && currentDiscount) {
        await fetch('/discount/CLEAR');
      }
    } catch (error) {
      console.error('Error applying tier discount:', error);
    }
  }

  /**
   * Maps item count to the highest matching tier discount code.
   */
  #getTierDiscountCode(count) {
    if (count >= 8) return 'PACK8';
    if (count >= 6) return 'PACK6';
    if (count >= 5) return 'PACK5';
    if (count >= 4) return 'PACK4';
    return '';
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}